import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { postIncentivesRecordReferralLinkClicked } from "api/methods";
import { Models } from "@/api/types";
import { Text } from "@/atoms";
import { PlatformLogo } from "@/components/PlatformLogo";
import { useTranslation } from "@/core/i18n";
import MobileViewportContainer from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import {
  ProductSlider,
  ProductSliderItem,
} from "@/modules/referralLanding/components/ProductSlider";
import { getSignupRewardDiscountType, getSignupRewardTitle } from "@/modules/rewards";
import { storage } from "@/modules/storage";
import { Button } from "atoms/button/Button";

import { getReferralRewardNewPatientRewardTitle } from "../referralRewards.utils";

type RewardType = "RepeatCash" | "DollarOff" | "PercentageOff" | "FreeItem" | "InOffice";

type ClaimReferralRewardContainerProps = {
  platform: Models<"PlatformModel">;
  platformLocationId?: string | null;
  referralCode: string;
  referringPerson: Models<"DisplayPersonAnonymous">;
  activeSignupReward: Models<"DisplayActiveReward"> | null;
  topListings: Models<"ListingModel">[];
  signupRewardInclusions?: Models<"ListingModel">[];
};

export const ClaimReferralRewardContainer = ({
  platform,
  platformLocationId,
  referralCode,
  referringPerson,
  activeSignupReward,
  topListings,
  signupRewardInclusions = [],
}: ClaimReferralRewardContainerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleClaimItem = () => {
    const currentPlatformId = platform?.id;

    const extractPlatformId = (id: any): string | undefined => {
      if (typeof id === "string") return id;
      if (id && typeof id === "object" && id.value) return id.value;
      return undefined;
    };

    const platformId = extractPlatformId(currentPlatformId);

    const baseUrl = `/signin?statelessReferralCode=${referralCode}`;
    const platformParam = platformId ? `&platformId=${platformId}` : "";
    const locationParam = platformLocationId ? `&locationId=${platformLocationId}` : "";

    navigate(`${baseUrl}${platformParam}${locationParam}`);
  };

  const { mutate: recordReferralLinkClicked } = useMutation({
    mutationFn: (statelessCode: string) =>
      postIncentivesRecordReferralLinkClicked({
        $data: statelessCode,
      }),
    onSuccess: () => {
      console.log("Referral signup link clicked");
    },
  });

  useEffect(() => {
    storage.setItem("statelessReferralCode", referralCode);

    if (referringPerson?.firstName) {
      storage.setItem("referralSenderName", referringPerson.firstName);
    }

    recordReferralLinkClicked(referralCode);
  }, [referralCode, recordReferralLinkClicked, referringPerson]);

  const getHeaderText = () => {
    if (!!referringPerson && activeSignupReward) {
      return getReferralRewardNewPatientRewardTitle(activeSignupReward, referringPerson.firstName);
    }

    if (activeSignupReward) {
      return getSignupRewardTitle(activeSignupReward);
    }

    return t("claimReferralRewardContainer.defaultHeader");
  };

  const getSubheaderText = () => {
    if (referringPerson) {
      return t("claimReferralRewardContainer.referringPersonSubheader", {
        referringPersonFirstName: referringPerson.firstName,
        platformName: platform?.name || t("claimReferralRewardContainer.defaultPlatformName"),
      });
    }

    return t("claimReferralRewardContainer.defaultSubheader");
  };

  const headerText = getHeaderText();

  const subheaderText = getSubheaderText();

  const renderWithActiveReward = () => {
    const displayListings =
      signupRewardInclusions && signupRewardInclusions.length > 0
        ? signupRewardInclusions
        : topListings;

    const subtextInstructions = (reward: Models<"DisplayActiveReward">) => {
      const rewardDiscountType = getSignupRewardDiscountType(reward);

      const amountOff = Number(reward.discount?.amountOff ?? 0) / 100;
      const discountPercentOff = Number(reward.discount?.percentOff ?? 0) * 100;

      if (rewardDiscountType === "AmountOff") {
        return t("claimReferralRewardContainer.amountOffSubtext", { amountOff });
      }

      if (rewardDiscountType === "PercentOff") {
        return t("claimReferralRewardContainer.percentOffSubtext", { discountPercentOff });
      }

      if (rewardDiscountType === "FreeItemAll") {
        return t("claimReferralRewardContainer.freeItemAllSubtext");
      }

      if (rewardDiscountType === "FreeItemSingle") {
        return t("claimReferralRewardContainer.freeItemSingleSubtext");
      }

      if (rewardDiscountType === "FreeItemChooseFrom") {
        return t("claimReferralRewardContainer.freeItemChooseFromSubtext");
      }

      return t("claimReferralRewardContainer.topSellingServicesSubtext");
    };

    return (
      <>
        <div className="flex flex-col items-center gap-4 pb-2 pt-6">
          <Text
            variant="body1"
            className="mb-2 text-center font-bold text-secondary"
            text={subtextInstructions(activeSignupReward!)}
          />
        </div>

        {displayListings.length === 1 ? (
          <button
            type="button"
            className="flex w-full justify-center"
            onClick={() => handleClaimItem()}
          >
            <ProductSliderItem listing={displayListings[0]} />
          </button>
        ) : (
          <ProductSlider listings={displayListings || []} onItemClick={handleClaimItem} />
        )}
      </>
    );
  };

  const renderWithoutActiveReward = () => {
    return (
      <>
        <div className="flex flex-col items-center gap-4 py-6">
          <Text
            variant="body1"
            className="text-center text-secondary"
            text={t("claimReferralRewardContainer.topSellersLabel")}
          />
        </div>

        <ProductSlider listings={topListings || []} onItemClick={handleClaimItem} />
      </>
    );
  };

  return (
    <MobileViewportContainer
      bottomCta={
        <div className="mb-4 flex flex-col items-center gap-4 px-6">
          <Button style="primary" size="large" fullWidth onClick={handleClaimItem}>
            {t("claimReferralRewardContainer.signUpButton")}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-4">
        <PlatformLogo
          logoUrl={platform?.logoImageUrl}
          platformName={platform?.name}
          className="mb-9 max-w-40"
        />
        <Text variant="v3-h1" className="text-center text-primary" text={headerText} />
        <Text variant="body2" className="text-center text-secondary" text={subheaderText} />
      </div>

      {activeSignupReward ? renderWithActiveReward() : renderWithoutActiveReward()}
    </MobileViewportContainer>
  );
};
